import {
    ChangeEvent,
    FC,
    InputHTMLAttributes,
    TextareaHTMLAttributes,
    useState,
} from 'react';
import { FieldHookConfig, useField } from 'formik';

import ReactDatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from 'components/contactForm/contactForm.module.css';

import { useTranslation } from 'react-i18next';
import { fr } from 'date-fns/locale/fr';
import { enGB } from 'date-fns/locale/en-GB';
import i18n from 'i18next';

import PhoneInput, { Value as E164Number } from 'react-phone-number-input';
import { MultiCheckboxList } from 'components/contactFormCustomFields/MultiCheckboxList';
import { SingleCheckboxList } from 'components/contactFormCustomFields/SingleCheckboxList';
import { FR_LANG } from 'components/langSwitcher/constants';

registerLocale('fr', fr);

type PhoneType = E164Number | undefined;

interface CustomCheckboxListProps {
    label: string;
    component: 'checkboxList';
    options: { key: string; label: string }[];
    multiSelect?: boolean;
    isbooknowform?: boolean;
}
interface CustomInputProps extends InputHTMLAttributes<HTMLInputElement> {
    label: string;
    component: 'input';
    isbooknowform?: boolean;
}

interface CustomTextareaProps
    extends TextareaHTMLAttributes<HTMLTextAreaElement> {
    label: string;
    component: 'textarea';
}

interface CustomPhoneNumberFieldProps {
    label: string;
    component: 'phoneNumber';
}

interface CustomDatepickerProps {
    label: string;
    component: 'datepicker';
}

type CustomFieldProps = (
    | CustomInputProps
    | CustomTextareaProps
    | CustomPhoneNumberFieldProps
    | (CustomCheckboxListProps & FieldHookConfig<string | string[]>)
    | (CustomDatepickerProps & FieldHookConfig<string | null>)
) &
    FieldHookConfig<string | string[] | null | boolean>;

export const CustomField: FC<CustomFieldProps & FieldHookConfig<string>> = ({
    label,
    component = 'input',
    ...props
}) => {
    const isBookNowForm =
        'isbooknowform' in props ? props.isbooknowform : undefined;

    const { t } = useTranslation();
    const [field, meta, helpers] = useField(props);

    const [hasContent, setHasContent] = useState(false);

    const isFrenchLangSelected = i18n.resolvedLanguage === FR_LANG;
    const currentLocale = isFrenchLangSelected ? fr : enGB;

    const inputStyles = `${styles.input} ${isBookNowForm ? styles.input_DarkText : null} ${hasContent ? styles.input_filled : null}`;

    const getErrorInputStyles = (error: string | undefined) => {
        if (error && meta.touched) {
            return {
                border: `1px solid red`,
            };
        }
    };
    const handleChange = (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        field.onChange(e);
        setHasContent(e.target.value.length > 0);
    };

    const commonProps = {
        ...field,
        ...props,
        className: inputStyles,
        onChange: handleChange,
    };

    const { setValue } = helpers;
    const handlePhoneChange = (value: PhoneType) => {
        value && setValue(value);
        setHasContent(!!value);
    };

    return (
        <>
            <label htmlFor={props.name}>{label}</label>
            {component === 'input' && (
                <input
                    {...(commonProps as InputHTMLAttributes<HTMLInputElement>)}
                    style={getErrorInputStyles(meta.error)}
                />
            )}
            {component === 'textarea' && (
                <textarea
                    rows={3}
                    {...(commonProps as TextareaHTMLAttributes<HTMLTextAreaElement>)}
                    style={getErrorInputStyles(meta.error)}
                />
            )}
            {component === 'phoneNumber' && (
                <PhoneInput
                    {...field}
                    id={props.id}
                    name={props.name}
                    international
                    defaultCountry="FR"
                    value={field.value as PhoneType}
                    onChange={handlePhoneChange}
                    className={inputStyles}
                    maxLength="20"
                    style={getErrorInputStyles(meta.error)}
                />
            )}
            {component === 'checkboxList' &&
                'options' in props &&
                props.options &&
                (props?.multiSelect ? (
                    <MultiCheckboxList
                        name={props.name}
                        options={props.options}
                    />
                ) : (
                    <SingleCheckboxList
                        name={props.name}
                        options={props.options}
                        isbooknowform={isBookNowForm}
                    />
                ))}

            {component === 'datepicker' && (
                <ReactDatePicker
                    locale={currentLocale}
                    selected={
                        typeof field.value === 'string' && field.value !== ''
                            ? new Date(field.value)
                            : null
                    }
                    onChange={(date) =>
                        setValue(date ? date.toISOString() : '')
                    }
                    className={styles.customDateInput}
                    calendarClassName={styles.datepicker}
                    dateFormat="dd-MM-yyyy"
                    placeholderText={t('contactForm.selectADate')}
                    showIcon={true}
                    toggleCalendarOnIconClick={true}
                    minDate={
                        new Date(new Date().setDate(new Date().getDate() + 1))
                    }
                />
            )}
        </>
    );
};
